import { applyPrintTokens, todayYmd } from './printLayout';
import { downloadBlob, safePrintFileName } from './printDownload';

function xmlEscape(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cellXml(text, { align = 'left', rowSpan = 1 } = {}) {
  const ha =
    align === 'right' ? 'Right' : align === 'center' ? 'Center' : 'Left';
  const span = rowSpan > 1 ? ` RowSpan="${rowSpan}"` : '';
  return `<CELL${span} Align="${ha}" Valign="Center"><P><TEXT>${xmlEscape(text)}</TEXT></P></CELL>`;
}

function printCtxOf(model, layout, ctx) {
  const title = layout?.title || model?.gridLabel || '목록';
  return {
    today: ctx?.today || todayYmd(),
    screenName: ctx?.screenName || title,
    tenantName: ctx?.tenantName || '',
    tenantCode: ctx?.tenantCode || '',
    userName: ctx?.userName || '',
    page: ctx?.page ?? 1,
    pageCount: ctx?.pageCount ?? 1,
  };
}

function bandLine(layout, ctx) {
  const left = applyPrintTokens(layout?.header?.left?.text, ctx);
  const right = applyPrintTokens(layout?.header?.right?.text, ctx);
  return [left, right].filter(Boolean).join('    ');
}

/**
 * 한글이 여는 HWPML(.hml) — rhwp 실패 시 내려받기 대체본
 */
export function buildPrintHml(model, layout, ctx = {}) {
  const title = layout?.title || model?.gridLabel || '목록';
  const columns = model?.columns || [];
  const rows = model?.rows || [];
  const mergeMap = model?.mergeMap || null;
  const showNo = layout?.showRowNumber !== false;
  const colCount = columns.length + (showNo ? 1 : 0) || 1;
  const printCtx = printCtxOf(model, layout, ctx);

  const headerCells = [
    showNo ? cellXml('No.', { align: 'center' }) : '',
    ...columns.map((c) => cellXml(c.header || c.field, { align: 'center' })),
  ].join('');

  const body = rows
    .map((row) => {
      const cells = [];
      if (showNo) cells.push(cellXml(String(row.index + 1), { align: 'center' }));
      columns.forEach((c) => {
        const info = mergeMap?.[c.field]?.[row.index];
        if (info?.skip) return;
        const align = c.align === 'right' || c.type === 'number' ? 'right' : c.align || 'left';
        cells.push(
          cellXml(row.cells[c.field] ?? '', {
            align,
            rowSpan: info?.rowSpan || 1,
          })
        );
      });
      return `<ROW>${cells.join('')}</ROW>`;
    })
    .join('');

  const metaLine = bandLine(layout, printCtx);
  const footer = applyPrintTokens(layout?.footer?.center?.text || layout?.footer?.left?.text, printCtx);

  return `<?xml version="1.0" encoding="UTF-8"?>
<?hwpml version="2.8"?>
<HWPML Style="embed">
  <HEAD>
    <DOCSUMMARY>
      <TITLE>${xmlEscape(title)}</TITLE>
    </DOCSUMMARY>
  </HEAD>
  <BODY>
    <SECTION>
      <P><TEXT>${xmlEscape(title)}</TEXT></P>
      ${metaLine ? `<P><TEXT>${xmlEscape(metaLine)}</TEXT></P>` : ''}
      <TABLE ColCount="${colCount}" RowCount="${rows.length + 1}">
        <ROW>${headerCells}</ROW>
        ${body}
      </TABLE>
      ${footer ? `<P><TEXT>${xmlEscape(footer)}</TEXT></P>` : ''}
    </SECTION>
  </BODY>
</HWPML>`;
}

let coreReady = null;

function ensureMeasureTextWidth() {
  const g = typeof window !== 'undefined' ? window : global;
  if (typeof g.measureTextWidth === 'function') return;
  let ctx = null;
  let lastFont = '';
  g.measureTextWidth = (font, text) => {
    if (typeof document === 'undefined') return String(text || '').length * 8;
    if (!ctx) ctx = document.createElement('canvas').getContext('2d');
    if (font !== lastFont) {
      ctx.font = font;
      lastFont = font;
    }
    return ctx.measureText(String(text ?? '')).width;
  };
}

async function ensureCore() {
  if (coreReady) return coreReady;
  coreReady = (async () => {
    ensureMeasureTextWidth();
    const mod = await import('@rhwp/core');
    const init = mod.default;
    await init({ module_or_path: '/rhwp_bg.wasm' });
    return mod;
  })();
  return coreReady;
}

function parseJson(raw) {
  if (raw && typeof raw === 'object') return raw;
  try {
    return JSON.parse(String(raw || '')) || {};
  } catch {
    return {};
  }
}

function flattenCells(model, layout) {
  const columns = model?.columns || [];
  const rows = model?.rows || [];
  const mergeMap = model?.mergeMap || null;
  const showNo = layout?.showRowNumber !== false;
  const header = [];
  if (showNo) header.push({ text: 'No.', align: 'center' });
  columns.forEach((c) => header.push({ text: c.header || c.field, align: 'center' }));
  const body = rows.map((row) => {
    const cells = [];
    if (showNo) cells.push({ text: String(row.index + 1), align: 'center' });
    columns.forEach((c) => {
      const info = mergeMap?.[c.field]?.[row.index];
      if (info?.skip) {
        cells.push({ text: '', align: 'left' });
        return;
      }
      const align = c.align === 'right' || c.type === 'number' ? 'right' : c.align || 'left';
      cells.push({ text: String(row.cells[c.field] ?? ''), align });
    });
    return cells;
  });
  return { header, body, colCount: header.length };
}

async function buildPrintHwpBytes(model, layout, ctx) {
  const { HwpDocument } = await ensureCore();
  const title = layout?.title || model?.gridLabel || '목록';
  const printCtx = printCtxOf(model, layout, ctx);
  const { header, body, colCount } = flattenCells(model, layout);
  if (!colCount) throw new Error('출력할 항목이 없습니다.');

  const doc = HwpDocument.createEmpty();
  try {
    doc.beginBatch?.();
    doc.insertText(0, 0, 0, title);
    const meta = bandLine(layout, printCtx);
    if (meta) {
      doc.insertParagraph(0, 1);
      doc.insertText(0, 1, 0, meta);
    }
    const tablePara = meta ? 2 : 1;
    doc.insertParagraph(0, tablePara);
    const tableInfo = parseJson(doc.createTable(0, tablePara, 0, body.length + 1, colCount));
    if (tableInfo.ok === false) throw new Error(tableInfo.message || '표 생성 실패');
    const paraIdx = Number(tableInfo.paraIdx ?? tablePara);
    const controlIdx = Number(tableInfo.controlIdx ?? 0);
    const put = (cellIdx, text) => {
      const t = String(text ?? '');
      if (!t) return;
      doc.insertTextInCell(0, paraIdx, controlIdx, cellIdx, 0, 0, t);
    };
    header.forEach((c, i) => put(i, c.text));
    body.forEach((row, r) => {
      row.forEach((c, i) => put((r + 1) * colCount + i, c.text));
    });
    doc.endBatch?.();
    const bytes = doc.exportHwp();
    if (!bytes?.length) throw new Error('HWP 바이트가 비었습니다.');
    return bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  } finally {
    try {
      doc.free?.();
    } catch {
      /* ignore */
    }
  }
}

/**
 * 바이너리 .hwp 내려받기. rhwp 실패 시 한글이 여는 .hml 로 대체.
 */
export async function downloadPrintHwp(model, layout, ctx = {}, fileName = '목록출력') {
  if (!model?.columns?.length) return { ok: false, reason: '출력할 항목을 선택하세요.' };
  try {
    const bytes = await buildPrintHwpBytes(model, layout, ctx);
    downloadBlob(
      new Blob([bytes], { type: 'application/x-hwp' }),
      safePrintFileName(fileName, 'hwp')
    );
    return { ok: true };
  } catch (e) {
    const hml = buildPrintHml(model, layout, ctx);
    downloadBlob(
      new Blob([`\uFEFF${hml}`], { type: 'application/xml;charset=utf-8' }),
      safePrintFileName(fileName, 'hml')
    );
    return { ok: true, reason: e?.message };
  }
}
